<template>
  <q-page padding>
    <div class="row q-col-gutter-md">
      <!-- Stats Cards -->
      <div class="col-12 col-md-3">
        <q-card class="stats-card">
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.activeCalls') }}</div>
            <div class="text-h4">{{ stats.activeCalls }}</div>
          </q-card-section>
        </q-card>
      </div>
      <div class="col-12 col-md-3">
        <q-card class="stats-card">
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.agentsOnline') }}</div>
            <div class="text-h4">{{ stats.onlineAgents }}</div>
          </q-card-section>
        </q-card>
      </div>
      <div class="col-12 col-md-3">
        <q-card class="stats-card">
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.waitingCalls') }}</div>
            <div class="text-h4">{{ stats.waitingCalls }}</div>
          </q-card-section>
        </q-card>
      </div>
      <div class="col-12 col-md-3">
        <q-card class="stats-card">
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.avgWaitTime') }}</div>
            <div class="text-h4">{{ stats.avgWaitTime }}s</div>
          </q-card-section>
        </q-card>
      </div>

      <!-- Recent Activity -->
      <div class="col-12 col-md-8">
        <q-card>
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.recentActivity') }}</div>
          </q-card-section>
          <q-card-section>
            <q-list separator>
              <q-item v-for="activity in recentActivity" :key="activity.id">
                <q-item-section avatar>
                  <q-icon :name="activity.icon" :color="activity.color" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ activity.description }}</q-item-label>
                  <q-item-label caption>{{ activity.time }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>
      </div>

      <!-- User Management -->
      <div class="col-12">
        <q-card>
          <q-card-section>
            <div class="row items-center justify-between">
              <div class="text-h6">{{ $t('dashboard.userManagement') }}</div>
              <q-btn color="primary" icon="person_add" :label="$t('dashboard.addUser')" @click="showUserDialog(false)" />
            </div>
          </q-card-section>
          <q-card-section>
            <q-table 
              v-if="users.length > 0"
              :rows="users" 
              :columns="userColumns" 
              row-key="id"
            >
              <template #body-cell-actions="{ row }">
                <q-td>
                  <q-btn-group flat>
                    <q-btn flat round icon="edit" @click="showUserDialog(true, row)" />
                    <q-btn flat round icon="delete" color="negative" @click="deleteUser(row)" />
                  </q-btn-group>
                </q-td>
              </template>
            </q-table>
            <p v-else class="text-grey-6 text-center q-mt-md">
              {{ $t("dashboard.noUsersFound") }}
            </p>
          </q-card-section>
        </q-card>
      </div>

      <!-- User Dialog -->
      <q-dialog v-model="userDialog.show">
        <q-card style="min-width: 400px">
          <q-card-section>
            <div class="text-h6">{{ userDialog.isEdit ? $t('dashboard.editUser') : $t('dashboard.addUser') }}</div>
          </q-card-section>
          <q-card-section>
            <q-form @submit="saveUser">
              <q-input v-model="userDialog.form.name" :label="$t('dashboard.userName')" required />
              <q-input v-model="userDialog.form.email" :label="$t('dashboard.userEmail')" type="email" required />
              <q-select v-model="userDialog.form.role" :options="['Admin', 'User']" :label="$t('dashboard.userRole')" required />
              <q-select v-model="userDialog.form.status" :options="['Active', 'Inactive']" :label="$t('dashboard.userStatus')" required />
              <q-card-actions align="right">
                <q-btn flat :label="$t('common.cancel')" color="primary" v-close-popup />
                <q-btn flat :label="$t('common.save')" type="submit" color="primary" />
              </q-card-actions>
            </q-form>
          </q-card-section>
        </q-card>
      </q-dialog>

      <!-- Quick Actions -->
      <div class="col-12 col-md-4">
        <q-card>
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.quickActions') }}</div>
          </q-card-section>
          <q-card-section>
            <div class="row q-col-gutter-sm">
              <div class="col-6">
                <q-btn color="primary" class="full-width" icon="phone" :label="$t('dashboard.newCall')" @click="startNewCall" />
              </div>
              <div class="col-6">
                <q-btn color="secondary" class="full-width" icon="chat" :label="$t('dashboard.newChat')" @click="startNewChat" />
              </div>
              <div class="col-6">
                <q-btn color="green" class="full-width" icon="whatsapp" :label="$t('dashboard.whatsapp')" @click="startNewWhatsApp" />
              </div>
              <div class="col-6">
                <q-btn color="blue" class="full-width" icon="message" :label="$t('dashboard.rcs')" @click="startNewRCS" />
              </div>
              <div class="col-6">
                <q-btn color="accent" class="full-width" icon="group" :label="$t('dashboard.teamMeeting')" @click="startTeamMeeting" />
              </div>
              <div class="col-6">
                <q-btn color="positive" class="full-width" icon="settings" :label="$t('dashboard.settings')" @click="openSettings" />
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>

      <!-- Call Dialog -->
      <q-dialog v-model="callDialog.show">
        <q-card style="min-width: 400px">
          <q-card-section>
            <div class="text-h6">Make a Call</div>
          </q-card-section>
          <q-card-section>
            <q-input
              v-model="callDialog.toNumber"
              label="Phone Number"
              filled
              outlined
            />
            <q-input
              v-model="callDialog.text"
              label="Message (Text-to-Speech)"
              filled
              outlined
            />
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat label="Cancel" color="negative" v-close-popup />
            <q-btn flat label="Call" color="primary" @click="makeCall" />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <!-- Messaging Dialog -->
      <q-dialog v-model="messageDialog.show">
        <q-card style="min-width: 400px">
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.send' + (messageDialog.type || 'Default') + 'Message') }}</div>
          </q-card-section>
          <q-card-section>
            <q-input v-model="messageDialog.to" :label="$t('dashboard.recipient')" filled outlined />
            <q-input v-model="messageDialog.text" :label="$t('dashboard.message')" filled outlined />
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat :label="$t('common.cancel')" color="negative" v-close-popup />
            <q-btn flat :label="$t('common.send')" color="primary" @click="sendMessage" />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <!-- Settings Dialog -->
      <q-dialog v-model="settingsDialog.show">
        <q-card style="min-width: 350px">
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.settings') }}</div>
          </q-card-section>
          <q-card-section>
            <div class="q-gutter-md">
              <q-select v-model="settingsDialog.theme" :options="themeOptions" :label="$t('dashboard.theme')" />
              <q-select v-model="settingsDialog.language" :options="languageOptions" :label="$t('dashboard.language')" />
              <q-toggle v-model="settingsDialog.notifications" :label="$t('dashboard.enableNotifications')" />
              <q-toggle v-model="settingsDialog.soundAlerts" :label="$t('dashboard.soundAlerts')" />
            </div>
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat :label="$t('common.cancel')" color="primary" v-close-popup />
            <q-btn flat :label="$t('common.save')" color="primary" @click="saveSettings" />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <!-- Team Meeting Dialog -->
      <q-dialog v-model="meetingDialog.show">
        <q-card style="min-width: 400px">
          <q-card-section>
            <div class="text-h6">{{ $t('dashboard.newTeamMeeting') }}</div>
          </q-card-section>
          <q-card-section>
            <q-form @submit.prevent="scheduleMeeting">
              <div class="q-gutter-md">
                <q-input
                  v-model="meetingDialog.title"
                  :label="$t('dashboard.meetingTitle')"
                  lazy-rules
                  :rules="[val => !!val || $t('dashboard.titleRequired')]"
                />
                <q-select
                  v-model="meetingDialog.participants"
                  :options="availableParticipants"
                  :label="$t('dashboard.participants')"
                  multiple
                  use-chips
                  lazy-rules
                  :rules="[val => val.length > 0 || $t('dashboard.participantsRequired')]"
                />
                <q-input
                  v-model="meetingDialog.date"
                  type="date"
                  :label="$t('dashboard.date')"
                  lazy-rules
                  :rules="[val => !!val || $t('dashboard.dateRequired')]"
                />
                <q-input
                  v-model="meetingDialog.time"
                  type="time"
                  :label="$t('dashboard.time')"
                  lazy-rules
                  :rules="[val => !!val || $t('dashboard.timeRequired')]"
                />
                <q-input
                  v-model="meetingDialog.agenda"
                  type="textarea"
                  :label="$t('dashboard.agenda')"
                />
              </div>
              <q-card-actions align="right">
                <q-btn flat :label="$t('common.cancel')" color="primary" v-close-popup />
                <q-btn flat :label="$t('common.schedule')" type="submit" color="primary" />
              </q-card-actions>
            </q-form>
          </q-card-section>
        </q-card>
      </q-dialog>
    </div>
  </q-page>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import { useVideoConferenceStore } from '../stores/videoConference'
import { updateUserPreferences } from '../services/userService'
import { useActivityLogService } from '../services/activityLog';
import * as statsService from '../services/statsService';
import { fetchDashboardStats, fetchOnlineAgents } from '../services/statsService';
import { getStats } from '../services/statsService';
import { sinchService } from '../services/sinch';
import { useAuthStore } from '../stores/auth';
import { messagingService } from "../services/messaging";

const router = useRouter()
const $q = useQuasar()
const { t } = useI18n()
const videoConferenceStore = useVideoConferenceStore()
const activityLogService = useActivityLogService()
const users = ref([])
const authStore = useAuthStore()
const userDialog = ref({
  show: false,  // Ensure 'show' is initialized
  isEdit: false,
  form: { name: '', email: '', role: '', status: 'Active' }
});

const userFilter = ref('');
const userColumns = ref([
  { name: 'name', label: t('dashboard.userName'), field: 'name', sortable: true },
  { name: 'email', label: t('dashboard.userEmail'), field: 'email', sortable: true },
  { name: 'role', label: t('dashboard.userRole'), field: 'role', sortable: true },
  { name: 'status', label: t('dashboard.userStatus'), field: 'status', sortable: true },
  { name: 'actions', label: t('common.actions'), field: 'actions' }
]);

const messageDialog = ref({
  show: false,
  type: "", // sms, chat, whatsapp, rcs
  to: "",
  text: "",
});

const openMessageDialog = (type) => {
  messageDialog.value.type = type;
  messageDialog.value.show = true;
};

const sendMessage = async () => {
  try {
    let sendFunction;
    switch (messageDialog.value.type) {
      case "sms":
        sendFunction = messagingService.sendSMS;
        break;
      case "chat":
        sendFunction = messagingService.sendChatMessage;
        break;
      case "whatsapp":
        sendFunction = messagingService.sendWhatsApp;
        break;
      case "rcs":
        sendFunction = messagingService.sendRCS;
        break;
      default:
        console.error("Unknown message type");
        return;
    }

    await sendFunction(messageDialog.value.to, messageDialog.value.text);
    messageDialog.value.show = false;
  } catch (error) {
    console.error("Error sending message:", error);
  }
};

// Use fetchOnlineAgents in the component
const fetchOnlineAgentsData = async () => {
  try {
    const onlineAgents = await fetchOnlineAgents();
    stats.value.onlineAgents = onlineAgents.length;
  } catch (error) {
    console.error("Error fetching online agents:", error);
    $q.notify({
      message: 'Failed to fetch online agents.',
      color: 'negative',
    });
  }
};


// State
const stats = ref({
  activeCalls: 0,
  onlineAgents: 0,
  waitingCalls: 0,
  avgWaitTime: 0
});

const recentActivity = ref([]);

const fetchStats = async () => {
  try {
    const data = await statsService.fetchDashboardStats()
    stats.value = {
      activeCalls: data.activeCalls || 0,
      onlineAgents: data.onlineAgents || 0,
      waitingCalls: data.waitingCalls || 0,
      avgWaitTime: data.avgWaitTime || 0
    }
  } catch (error) {
    console.error('Error fetching dashboard stats:', error)
    $q.notify({ message: 'Failed to fetch stats. Please try again later.', color: 'negative' })
  }
}

const fetchRecentActivity = async () => {
  try {
    const activities = await useActivityLogService().getRecentActivity()
    recentActivity.value = activities.map(activity => ({
      id: activity.id,
      icon: activity.type === 'call' ? 'phone' : activity.type === 'chat' ? 'chat' : 'event_note',
      color: activity.type === 'call' ? 'primary' : activity.type === 'chat' ? 'secondary' : 'accent',
      description: activity.description,
      time: new Date(activity.timestamp).toLocaleString()
    }))
  } catch (error) {
    console.error('Error fetching recent activity:', error)
    $q.notify({ message: t('dashboard.errors.fetchRecentActivity'), color: 'negative' })
  }
}

// **Format Timestamp**
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString(); // Format for better readability
};

// Reactive state for call dialog
const callDialog = ref({
  show: false,
  toNumber: '',
  text: ''
});

// Open Call Dialog
const openCallDialog = () => {
  callDialog.value.show = true;
};

const settingsDialog = ref({
  show: false,
  theme: 'light',
  language: 'en',
  notifications: true,
  soundAlerts: true
})

const meetingDialog = ref({
  show: false,
  title: '',
  participants: [],
  date: '',
  time: '',
  agenda: ''
})

const themeOptions = [
  { label: t('dashboard.themes.light'), value: 'light' },
  { label: t('dashboard.themes.dark'), value: 'dark' }
]

const languageOptions = [
  { label: 'English', value: 'en' },
  { label: 'Español', value: 'es' },
  { label: 'Français', value: 'fr' }
]

// Available Participants
const availableParticipants = ref([]);

// Fetch participants (e.g., from a Sinch API or Firestore)
const fetchParticipants = async () => {
  try {
    availableParticipants.value = await sinchService.fetchParticipants();
  } catch (error) {
    console.error('Error fetching participants:', error);
  }
};


// **Start New Call**
const startNewCall = () => {
  callDialog.value.show = true;
  $q.notify({ message: t('dashboard.notifications.callStarted'), color: 'positive' });
};

// Function to make a voice call via Sinch API
const makeCall = async () => {
  try {
    if (!callDialog.value.toNumber) {
      $q.notify({ message: t('dashboard.errors.invalidPhoneNumber'), color: 'negative' });
      return;
    }

    console.log("Making call to:", callDialog.value.toNumber);

    const result = await sinchService.makeCall(callDialog.value.toNumber, callDialog.value.text);
    
    if (!result || result.error) {
      console.error("Sinch API Error:", result);
      throw new Error(result.error || "Failed to make call");
    }

    $q.notify({
      message: t('dashboard.notifications.callInitiated', { callId: result.callId }),
      color: 'positive'
    });

    callDialog.value.show = false;
  } catch (error) {
    console.error('Error initiating call:', error);
    $q.notify({
      message: t('dashboard.errors.callFailed', { error: error.message }),
      color: 'negative'
    });
  }
};

const startNewChat = () => {
  router.push('/chat');
  $q.notify({ message: t('dashboard.notifications.chatStarted'), color: 'info' });
};

// **Open Team Meeting Modal**
const startTeamMeeting = async () => {
  try {
    meetingDialog.value.show = true;
    $q.notify({ message: t('dashboard.notifications.meetingStarted'), color: 'accent' });
  } catch (error) {
    console.error('Error starting team meeting:', error);
    $q.notify({
      message: t('dashboard.errors.startMeetingFailed', { error: error.message }),
      color: 'negative'
    });
  }
};

// Schedule Meeting
const scheduleMeeting = async () => {
  if (!meetingDialog.value.title || !meetingDialog.value.participants.length || !meetingDialog.value.date || !meetingDialog.value.time) {
    $q.notify({ message: t('dashboard.errors.scheduleMeetingValidation'), color: 'negative' });
    return;
  }

  try {
    const meetingData = {
      title: meetingDialog.value.title,
      participants: meetingDialog.value.participants,
      date: meetingDialog.value.date,
      time: meetingDialog.value.time,
      agenda: meetingDialog.value.agenda
    };

    await sinchService.createConferenceCall(meetingData);

    $q.notify({ message: t('dashboard.notifications.meetingScheduled'), color: 'positive' });

    meetingDialog.value = { show: false, title: '', participants: [], date: '', time: '', agenda: '' };
  } catch (error) {
    console.error('Error scheduling meeting:', error);
    $q.notify({ message: t('dashboard.errors.scheduleMeeting', { error: error.message }), color: 'negative' });
  }
};

// **Open Settings Dialog**
const openSettings = () => {
  settingsDialog.value.show = true;
  $q.notify({ message: t('dashboard.notifications.settingsOpened'), color: 'positive' });
};

const saveSettings = async () => {
  try {
    // Save user preferences
    await updateUserPreferences(settingsDialog.value)
    
    $q.notify({
      message: t('dashboard.notifications.settingsSaved'),
      color: 'positive'
    })
    
    settingsDialog.value.show = false
  } catch (error) {
    $q.notify({
      message: t('dashboard.errors.saveSettings', { error: error.message }),
      color: 'negative'
    })
  }
}

const fetchRealTimeStats = async () => {
  try {
    const data = await fetchDashboardStats(); // Ensure function is called correctly
    stats.value = {
      activeCalls: data.activeCalls || 0,
      onlineAgents: data.onlineAgents || 0,
      waitingCalls: data.waitingCalls || 0,
      avgWaitTime: data.avgWaitTime || 0,
    };
  } catch (error) {
    console.error("Error fetching real-time stats:", error.message);
    $q.notify({
      message: t("dashboard.errors.fetchStats"),
      color: "negative",
    });
  }
};

const showUserDialog = (isEdit, user = {}) => {
  userDialog.value = {
    show: true,  // Ensure 'show' is always set
    isEdit,
    form: isEdit
      ? { ...user }
      : { name: '', email: '', role: '', status: 'Active' }
  };
};

const saveUser = async () => {
  if (!userDialog.value.form.name || !userDialog.value.form.email) {
    $q.notify({ message: t("dashboard.errors.saveUserValidation"), color: "negative" });
    return;
  }

  try {
    const userRef = userDialog.value.isEdit
      ? doc(db, "users", userDialog.value.form.id)
      : doc(collection(db, "users"));

    await setDoc(userRef, userDialog.value.form, { merge: true });

    $q.notify({ message: t("dashboard.notifications.userSaved"), color: "positive" });
    fetchUsers();
  } catch (error) {
    console.error("Error saving user:", error);
    $q.notify({ message: t("dashboard.errors.saveUser"), color: "negative" });
  } finally {
    userDialog.value.show = false;
  }
};

const deleteUser = (user) => {
  if (authStore.role !== 'Admin') {
    $q.notify({ message: t('dashboard.errors.permissionDenied'), color: 'negative' });
    return;
  }

  $q.dialog({
    title: t('dashboard.confirmDelete'),
    message: t('dashboard.confirmDeleteUser', { name: user.name }),
    cancel: true,
    persistent: true
  }).onOk(async () => {
    try {
      await deleteDoc(doc(db, 'users', user.id));
      $q.notify({ message: t('dashboard.notifications.userDeleted'), color: 'positive' });
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      $q.notify({ message: t('dashboard.errors.deleteUser'), color: 'negative' });
    }
  });
};

const fetchUsers = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'users'));
    users.value = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching users:', error);
    $q.notify({ message: t('dashboard.errors.fetchUsers'), color: 'negative' });
  }
};

let statsInterval;  // Declare once at the top

onMounted(() => {
  fetchRealTimeStats();
  fetchRecentActivity();
  fetchStats();
  fetchOnlineAgentsData();

  statsInterval = setInterval(fetchStats, 30000); // Update every 30 seconds
});

onUnmounted(() => {
  if (statsInterval) {
    clearInterval(statsInterval);
  }
});
</script>

<style scoped>
stats-card {
  background: linear-gradient(145deg, #1976D2 0%, #64B5F6 100%);
  color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stats-card .text-h4 {
  font-size: 2.5rem;
  font-weight: 500;
}

.stats-card .text-h6 {
  opacity: 0.9;
}

.q-card-section {
  padding: 16px;
  text-align: center;
}

.q-card {
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.q-btn {
  min-width: 100px;
}

.q-gutter-md {
  margin-bottom: 10px;
}

.q-input {
  margin-bottom: 10px;
}

.q-list {
  max-height: 300px;
  overflow-y: auto;
}

.q-item {
  transition: background 0.3s;
}

.q-item:hover {
  background: rgba(0, 0, 0, 0.05);
}

.q-icon {
  font-size: 24px;
}

.q-item-label {
  font-size: 14px;
}
</style>